import React, { FunctionComponent, useEffect, useState } from 'react';
import {
  Box,
  FormControl,
  Grid,
  Stack,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { v1 as uuidv1 } from 'uuid';
import { useAppSelector } from '../../../../hooks';
import { getAuthState } from '../../../../store/AuthSlice';
import BasicLoader from '../../../../components/Loaders/BasicLoader';
import { IOneToOneTutorRateVersion } from '../../../../store/V2/OneToOneTutorRateVersionsSlice';
import { TPTextField } from '../../../../components/Inputs';
import {
  getOneToOneTutorRatesState,
  IOneToOneTutorRate,
} from '../../../../store/V2/OneToOneTutorRatesSlice';
import { IOneToOneTutorRateStudentType } from '../../../../store/V2/OneToOneTutorRateStudentTypesSlice';

interface ICustomTutorRateAmount {
  id: string;
  rateVersion: string;
  amount: string;
  currency: string;
  hideToUser: boolean;
  defaultAmount: string;
  defaultCurrency: string;
}

interface ICustomTutorRate {
  id: string;
  tutorCountry: string;
  studentCountry: string;
  amounts: ICustomTutorRateAmount[];
  studentType: {
    name: string;
    grades: Array<{ curriculum: string; grade: string }>;
  };
}

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const OneToOneRatesSection: FunctionComponent = () => {
  // Store
  const authState = useAppSelector(getAuthState);
  const oneToOneTutorRatesState = useAppSelector(getOneToOneTutorRatesState);

  // State
  const [userRates, setUserRates] = useState<ICustomTutorRate[]>([]);

  const authUser = authState.user;
  const userCountry = authUser ? authUser.country : undefined;

  useEffect(() => {
    setUserRates(
      convertTutorRatesToCustomData(oneToOneTutorRatesState.oneToOneTutorRates)
    );
  }, [oneToOneTutorRatesState.oneToOneTutorRates]);

  const convertTutorRatesToCustomData = (
    tutorRateList: IOneToOneTutorRate[]
  ) => {
    const list: ICustomTutorRate[] = [];
    const userTutorRateList =
      authUser &&
      authUser.oneToOneTutorRates &&
      authUser.oneToOneTutorRates.length
        ? authUser.oneToOneTutorRates
        : [];

    tutorRateList.forEach((tutorRateItem) => {
      const matchingUserTutorRate = userTutorRateList.find(
        (userTutorRate) =>
          userTutorRate.tutorCountry === tutorRateItem.tutorCountry &&
          userTutorRate.studentCountry === tutorRateItem.studentCountry &&
          userTutorRate.studentType ===
            (tutorRateItem.studentType as IOneToOneTutorRateStudentType).name
      );

      const studentTypeData = tutorRateItem.studentType as IOneToOneTutorRateStudentType;
      const customTutorRate: ICustomTutorRate = {
        id: uuidv1(),
        tutorCountry: tutorRateItem.tutorCountry,
        studentCountry: tutorRateItem.studentCountry,
        studentType: {
          name: studentTypeData.name,
          grades: studentTypeData.grades,
        },
        amounts: tutorRateItem.amounts.map((amountItem) => {
          const tutorRateVersionData = amountItem.rateVersion as IOneToOneTutorRateVersion;

          const newAmountItem = {
            id: uuidv1(),
            rateVersion: tutorRateVersionData.name,
            amount: '',
            currency: amountItem.currency,
            hideToUser: tutorRateVersionData.hideToUser,
            defaultAmount: amountItem.amount.toString(),
            defaultCurrency: amountItem.currency,
          };

          if (matchingUserTutorRate) {
            const matchingAmount = matchingUserTutorRate.amounts.find(
              (userRateAmountItem) =>
                userRateAmountItem.rateVersion === tutorRateVersionData.name
            );

            if (
              matchingAmount &&
              matchingAmount.currency &&
              matchingAmount.amount
            ) {
              newAmountItem.amount = matchingAmount.amount.toString();
              newAmountItem.currency = matchingAmount.currency;
              newAmountItem.hideToUser = matchingAmount.hideToUser;
            }
          }

          return newAmountItem;
        }),
      };

      list.push(customTutorRate);
    });

    return list;
  };

  return (
    <Box>
      <Stack
        flexDirection="row"
        justifyContent="space-between"
        marginBottom="24px"
      >
        <Typography variant="h6" fontSize="18px">
          One to One Class Rates
        </Typography>
      </Stack>
      {!oneToOneTutorRatesState.oneToOneTutorRates.length &&
      oneToOneTutorRatesState.status === 'loading' ? (
        <div style={{ padding: '30px' }}>
          <BasicLoader />
        </div>
      ) : null}
      <div>
        <Grid container spacing={3}>
          {userRates.map((rateItem, index) => {
            let studentCountry = 'Local';

            if (rateItem.studentCountry !== userCountry) {
              studentCountry = rateItem.studentCountry;
            }

            const amountList = rateItem.amounts;
            const studentType = rateItem.studentType as IOneToOneTutorRateStudentType;
            const typeName = `${studentCountry} - ${studentType.name}`;

            return [
              <Grid item xs={12} key={`rate-item-${index}`}>
                <Typography fontWeight={600} fontSize="14px">
                  {typeName}
                </Typography>
              </Grid>,
              <Grid item xs={12} key={`rate-item-fields-continer-${index}`}>
                <div>
                  <Grid container spacing={2}>
                    {amountList.map((amountItem, subIndex) => {
                      const valueText = `${amountItem.currency} ${
                        amountItem.amount || amountItem.defaultAmount
                      }`;

                      return (
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          key={`rate-item-fields-continer-${index}-heading-${subIndex}`}
                        >
                          <FormControl fullWidth>
                            <div style={{ marginBottom: '10px' }}>
                              <Typography fontWeight={600} fontSize="14px">
                                {amountItem.rateVersion}
                              </Typography>
                            </div>
                            <TPTextField
                              {...textFieldProps}
                              placeholder={valueText}
                              defaultValue={valueText}
                              InputProps={{ readOnly: true }}
                            />
                          </FormControl>
                        </Grid>
                      );
                    })}
                  </Grid>
                </div>
              </Grid>,
            ];
          })}
          {!userRates.length && oneToOneTutorRatesState.status !== 'loading' ? (
            <Grid item xs={12}>
              <Typography>No one to one rates are available</Typography>
            </Grid>
          ) : null}
        </Grid>
      </div>
    </Box>
  );
};

export default OneToOneRatesSection;
